import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Check, Copy } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Icon } from '../Icon';

export type CodeFieldProps = {
  label: ReactNode;
  value: string;
  /** Show only the last four characters (an API key after its first display). Copying still copies the full value. */
  masked?: boolean;
  copyLabel: ReactNode;
  copiedLabel: ReactNode;
  /** Called after a successful copy; show a Toast from here. */
  onCopy?: () => void;
  help?: ReactNode;
  className?: string;
};

/** The masked form of a secret: bullets then the last four characters. Pure, exported for tests. */
export function maskSecret(value: string): string {
  return value.length <= 4 ? '••••' : `${'•'.repeat(Math.min(12, value.length - 4))}${value.slice(-4)}`;
}

/** A read-only monospace value with a copy button: API keys, embed snippets, connector URLs. */
export function CodeField({
  label,
  value,
  masked = false,
  copyLabel,
  copiedLabel,
  onCopy,
  help,
  className,
}: CodeFieldProps) {
  const id = `cf${useId().replace(/:/g, '')}`;
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      return;
    }
    setCopied(true);
    onCopy?.();
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={cx('flex flex-col gap-1.5', className)}>
      <span id={`${id}-label`} className="text-sm font-semibold text-jc-ink">
        {label}
      </span>
      <div className="flex items-stretch overflow-hidden rounded-jc-field ring-1 ring-inset ring-jc-border-control">
        <code
          aria-labelledby={`${id}-label`}
          className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap bg-jc-elevated px-3 py-2.5 font-mono text-sm text-jc-accent"
        >
          {masked ? maskSecret(value) : value}
        </code>
        <button
          type="button"
          onClick={copy}
          className="inline-flex shrink-0 items-center gap-1.5 border-l border-jc-border-control bg-jc-surface px-3 text-sm font-semibold text-jc-ink hover:bg-jc-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-jc-focus"
        >
          <Icon icon={copied ? Check : Copy} size="sm" />
          <span aria-live="polite">{copied ? copiedLabel : copyLabel}</span>
        </button>
      </div>
      {help ? <span className="text-xs text-jc-ink-secondary">{help}</span> : null}
    </div>
  );
}
